import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { TestType } from '@risksmart-app/domain/src/types/consts/test-type';
import type { UpdateControlTestInternalAuditResultMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

export type UpdateControlTestInternalAuditResultInput = {
  Id: string;
  OriginalTimestamp: string;
  ParentControlId: string;
  Title?: string | null;
  TestType?: TestType | null;
  Description?: string | null;
  DesignEffectiveness?: number | null;
  PerformanceEffectiveness?: number | null;
  OverallEffectiveness?: number | null;
  Submitter: string;
  TestDate?: string | null;
  CustomAttributeData?: CustomAttributeData | null;
};

const mapTrpcResponseToGraphQL = (trpcData: {
  Id: string;
}): UpdateControlTestInternalAuditResultMutation => ({
  updateControlTestInternalAuditResultApi: {
    __typename: 'IdOutput',
    Id: trpcData.Id,
  },
});

export const useUpdateControlTestInternalAuditResultTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.internalAuditResult.updateControlTest.mutationOptions({
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.internalAuditResult.pathKey(),
          }),
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.assessment.internalAuditResultCount.queryKey(),
          }),
          // COI-471 parity: Apollo twin also evicts `internal_audit_report`.
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.internalAuditReport.pathKey(),
          }),
          // COI-471 parity: Apollo twin also evicts `control`.
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.control.pathKey(),
          }),
        ]);
      },
    }),
  });

  return {
    updateControlTestInternalAuditResult: async (
      variables: UpdateControlTestInternalAuditResultInput
    ): Promise<UpdateControlTestInternalAuditResultMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
